import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  ReportProblem as ReportProblemIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import OrganisationAdminNavBar from "./OrganisationAdminNavBar";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { firestore, auth } from "../../../firebase";
import { onAuthStateChanged } from "firebase/auth";

const AVATAR_COLORS = ["#2E7D32", "#7B1FA2", "#1565C0", "#EF6C00", "#00838F", "#6D4C41"];

const getAvatarColor = (seed = "U") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const ROLE_UI_LABELS = {
  "OU Admin": "Organisation Admin",
  Admin: "Organisation Admin",
  organisation_admin: "Organisation Admin",
  "OU Operator": "Organisation Operator",
  Operator: "Organisation Operator",
  organisation_operator: "Organisation Operator",
  "OU User": "Organisation Operator",
  User: "Organisation Operator",
};

const getRoleLabel = (role) => ROLE_UI_LABELS[role] || role;

const isRoleMatch = (role, filterRole) => {
  if (filterRole === "All Roles") return true;
  if (["Admin", "OU Admin"].includes(filterRole)) {
    return ["Admin", "OU Admin", "organisation_admin"].includes(role);
  }
  if (["Operator", "Organisation Operator", "User", "OU User"].includes(filterRole)) {
    return [
      "Operator",
      "OU Operator",
      "organisation_operator",
      "User",
      "OU User",
    ].includes(role);
  }
  return role === filterRole;
};

const OrganisationAdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("All Roles");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [users, setUsers] = useState([]);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userData, setUserData] = useState({
    displayName: "",
    email: "",
    phoneNumber: "",
    role: "organisation_operator",
    status: "Active",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Search for user by email as doc ID may not be UID
          const q = query(
            collection(firestore, "UserProfileMaster"),
            where("email", "==", user.email)
          );
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            const data = userDoc.data();
            setCurrentAdmin({ uid: user.uid, id: userDoc.id, ...data });

            if (data.tenantId) {
              const tenantDoc = await getDoc(doc(firestore, "TenantMaster", data.tenantId));
              if (tenantDoc.exists()) {
                setCurrentAdmin((prev) => ({ ...prev, ...tenantDoc.data() }));
              }
            }
          }
        } catch (error) {
          console.error("Error fetching current admin data:", error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentAdmin?.tenantId) return;

    const q = query(
      collection(firestore, "UserProfileMaster"),
      where("tenantId", "==", currentAdmin.tenantId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
    });

    return () => unsubscribe();
  }, [currentAdmin]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const tabRoleMap = ["All Roles", "Operator"];
    const matchesTab = activeTab === 0 || isRoleMatch(user.role, tabRoleMap[activeTab]);
    
    const matchesRole = isRoleMatch(user.role, roleFilter);
    const matchesStatus = statusFilter === "All Status" || user.status === statusFilter;

    return matchesSearch && matchesTab && matchesRole && matchesStatus;
  });

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserData({
        displayName: user.displayName || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
        role: user.role || "organisation_operator",
        status: user.status || "Active",
      });
    } else {
      setEditingUser(null);
      setUserData({
        displayName: "",
        email: "",
        phoneNumber: "",
        role: "organisation_operator",
        status: "Active",
      });
    }
    setOpenDialog(true);
  };

  const handleSaveUser = async () => {
    if (!userData.displayName || !userData.email) {
      setSnackbar({ open: true, message: "Name and Email are required", severity: "error" });
      return;
    }

    if (!currentAdmin?.tenantId) {
      setSnackbar({ open: true, message: "Error: Admin tenant ID not found", severity: "error" });
      return;
    }

    try {
      if (editingUser) {
        await updateDoc(doc(firestore, "UserProfileMaster", editingUser.id), {
          ...userData,
          updatedAt: new Date(),
        });
        setSnackbar({ open: true, message: "User updated successfully", severity: "success" });
      } else {
        const newUserData = {
          ...userData,
          tenantId: currentAdmin.tenantId,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        await addDoc(collection(firestore, "UserProfileMaster"), newUserData);
        setSnackbar({ open: true, message: "User added successfully", severity: "success" });
      }
      setOpenDialog(false);
    } catch (error) {
      console.error("Error saving user:", error);
      setSnackbar({ open: true, message: "Error saving user", severity: "error" });
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteDoc(doc(firestore, "UserProfileMaster", id));
        setSnackbar({ open: true, message: "User deleted successfully", severity: "success" });
      } catch (error) {
        setSnackbar({ open: true, message: "Error deleting user", severity: "error" });
      }
    }
  };

  const stats = [
    {
      label: "Total Operators",
      value: users.length,
      sub: "+3 this month",
      icon: <PeopleIcon sx={{ color: "#673ab7" }} />,
      tag: "All",
      tagColor: "#e3f2fd",
      tagTextColor: "#1976d2",
    },
    {
      label: "Organisation Operators",
      value: users.filter((u) => isRoleMatch(u.role, "Operator")).length,
      sub: `${users.filter((u) => isRoleMatch(u.role, "Operator") && u.status === "Inactive").length} inactive`,
      icon: <PersonIcon sx={{ color: "#3f51b5" }} />,
      tag: "Active",
      tagColor: "#e8f5e9",
      tagTextColor: "#4caf50",
    },
    {
      label: "Pending Tasks",
      value: 5,
      sub: "Awaiting action",
      icon: <AssignmentIcon sx={{ color: "#ff9800" }} />,
      tag: "Open",
      tagColor: "#fff3e0",
      tagTextColor: "#ff9800",
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f5f7fb", minHeight: "100vh" }}>
      <OrganisationAdminNavBar />
      
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", color: "#333" }}>
            Organisation Admin
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<ReportProblemIcon />}
              onClick={() => navigate("/complaints_list")}
              sx={{
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              Complaints
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: "linear-gradient(90deg, #2196F3 0%, #9C27B0 100%)",
                borderRadius: "10px",
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              Add Organisation User
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ borderRadius: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: "rgba(103, 58, 183, 0.1)", borderRadius: "15px" }}>
                      {stat.icon}
                    </Box>
                    <Chip
                      label={stat.tag}
                      size="small"
                      sx={{ bgcolor: stat.tagColor, color: stat.tagTextColor, fontWeight: "bold" }}
                    />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.sub}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ borderRadius: "20px", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: "bold", fontSize: "1rem" } }}>
              <Tab label="All Users" />
              <Tab label="Operators" />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
                    sx: { borderRadius: "10px", bgcolor: "#f8f9fa" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <Select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    sx={{ borderRadius: "10px", bgcolor: "#f8f9fa" }}
                  >
                    <MenuItem value="All Roles">All Roles</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Operator">Operator</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ borderRadius: "10px", bgcolor: "#f8f9fa" }}
                  >
                    <MenuItem value="All Status">All Status</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: "bold", color: "text.secondary", borderBottom: "2px solid #f0f0f0" } }}>
                    <TableCell>USER</TableCell>
                    <TableCell>ROLE</TableCell>
                    <TableCell>PHONE</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell>JOINED</TableCell>
                    <TableCell align="right">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} sx={{ "&:hover": { bgcolor: "#fcfcfc" } }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ bgcolor: getAvatarColor(user.email || user.displayName || user.id) }}>
                            {user.displayName?.charAt(0) || "U"}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                              {user.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleLabel(user.role)}
                          size="small"
                          sx={{
                            bgcolor: isRoleMatch(user.role, "OU Admin") ? "#f3e5f5" : isRoleMatch(user.role, "Operator") ? "#e3f2fd" : "#fff3e0",
                            color: isRoleMatch(user.role, "OU Admin") ? "#9c27b0" : isRoleMatch(user.role, "Operator") ? "#1976d2" : "#ff9800",
                            fontWeight: "bold",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.phoneNumber || "N/A"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status || "Active"}
                          size="small"
                          sx={{
                            bgcolor: user.status === "Inactive" ? "#f5f5f5" : "#e8f5e9",
                            color: user.status === "Inactive" ? "#757575" : "#4caf50",
                            fontWeight: "bold",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : "Jan 12, 2025"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredUsers.length} of {users.length} users
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* User Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingUser ? "Edit Organisation Operator" : "Add Organisation Operator"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Full Name"
              fullWidth
              value={userData.displayName}
              onChange={(e) => setUserData({ ...userData, displayName: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              value={userData.email}
              onChange={(e) => setUserData({ ...userData, email: e.target.value })}
            />
            <TextField
              label="Phone Number"
              fullWidth
              value={userData.phoneNumber}
              onChange={(e) => setUserData({ ...userData, phoneNumber: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={getRoleLabel(userData.role)}
                label="Role"
                onChange={(e) => {
                  const selectedRole = e.target.value;
                  const storedRole = selectedRole === "OU Admin"
                    ? "organisation_admin"
                    : selectedRole === "Organisation Operator"
                        ? "organisation_operator"
                        : selectedRole;
                  setUserData({ ...userData, role: storedRole });
                }}
              >
                <MenuItem value="OU Admin">Organisation Admin</MenuItem>
                <MenuItem value="Organisation Operator">Organisation Operator</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={userData.status}
                label="Status"
                onChange={(e) => setUserData({ ...userData, status: e.target.value })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUser} sx={{ borderRadius: "10px" }}>
            Save User
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrganisationAdminPage;
